import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { Map as ImmutableMap, Record } from 'immutable';
import React, { type MouseEvent, useEffect, useRef, useState } from 'react';

// App-Components
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { scheduledTransactionsActions, scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';

import { billPresentmentSelectors } from 'app-components/flux/bill-presentment';

// MUI
import ThreeDotIcon from '@mui/icons-material/MoreVert';
import type { IconButtonProps } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { HandleDisconnectOption, HandleEndOption } from 'components/BillPresentment/utils';
// COMPONENTS
import { doBillPresentment } from 'components/Dialogs/BillPresentment/actions';
import { DIALOG_TYPE as DIALOG_CONFIRMATION, mkConfirmationDialogProps } from 'components/Dialogs/ConfirmationDialog';
import { showViewSeriesDialog } from 'components/Dialogs/ViewSeriesDialog/actions';
import NestedQMenu from 'components/NestedQMenu';
import { QButtonVariantEnum } from 'components/QButton';
import useQDialogs from 'components/QDialogs/useQDialogs';
import ScheduledTransactionEditDialog from 'components/ScheduledTransactions/ScheduledTransactionEditDialog';

const location = 'settings recurring';

type RecurringMenuProps = {
  id?: string;
  model: scheduledTransactionsTypes.ScheduledTransaction;
  index: number;
  variant?: 'standard' | 'tax';
  slotProps?: { iconButton?: Partial<IconButtonProps> };
};

function RecurringMenu(props: RecurringMenuProps) {
  const { id, model, index, variant = 'standard', slotProps } = props;
  const dispatch = useAppDispatch();
  const { dialogAlert } = useQDialogs();
  const menuRef = useRef<HTMLButtonElement | null>(null);
  const exist = Boolean(model.id);
  const theme = useTheme();

  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const connectOption = useAppSelector((state) => billPresentmentSelectors.getShowConnectOption(state, model));

  const hasBPS = Boolean(model.presentmentAccountId);
  const isBillerConnecting = hasBPS && model.billerEnrollmentStatus === 'PENDING';

  useEffect(() => {
    if (isBillerConnecting && menuOpen) {
      setMenuOpen(false);
    }
  }, [isBillerConnecting, menuOpen]);

  const openMenu = (e: MouseEvent) => {
    e.stopPropagation();
    if (isBillerConnecting) {
      return;
    }
    setMenuOpen(true);
  };
  const closeMenu = () => setMenuOpen(false);

  let billText;
  const reconnect =
    model?.presentmentAccountId &&
    (model.billerEnrollmentStatus === 'FAILED' || model.billerEnrollmentStatus === 'DISCONNECTED');
  if (reconnect) {
    billText = 'Reconnect biller';
  } else if (connectOption) {
    billText = 'Connect biller';
  }

  // ### -------------------------------------- ###
  // ###             Menu Functions             ###
  // ### -------------------------------------- ###
  const makeDialog = (data: any) => dispatch(rootUiActions.createDialog(rootUiTypes.mkRootUiData(data)));

  const confirmEndSeries = () => {
    makeDialog({
      id: 'end-series-confirmation-settings',
      type: DIALOG_CONFIRMATION,
      props: ImmutableMap(
        mkConfirmationDialogProps({
          title: 'End series',
          content: (
            <Typography variant='bodyRegular' id='confirmation-dialog-description'>
              Ending your&nbsp;<b>{model?.transaction?.payee || 'this transaction'}</b>&nbsp;series will only mark it as
              ended. It will not delete your series or any history related to it.
            </Typography>
          ),
          confirmLabel: 'End Series',
          confirmAction: (_reason, _closeEvent) => {
            HandleEndOption(dispatch, model, location, true);
          },
          denyLabel: 'Cancel',
        }),
      ),
    });
    closeMenu();
    tracker.track(tracker.events.recurringMenu, { action: 'endSeries', variant, context: id });
  };

  const confirmDeleteSeries = () => {
    makeDialog({
      id: 'delete-series-confirmation-settings',
      type: DIALOG_CONFIRMATION,
      props: ImmutableMap(
        mkConfirmationDialogProps({
          title: 'Delete recurring series',
          content: `Deleting the recurring series for ${model?.transaction?.payee} will delete all future reminders for this series.`,
          confirmLabel: 'Delete',
          confirmButtonVariant: QButtonVariantEnum.DELETE,
          confirmAction: (_reason, _closeEvent) => {
            dispatch(
              scheduledTransactionsActions.deleteScheduledTransaction(
                scheduledTransactionsTypes.mkScheduledTransactionPatch({ id: model.id, isDeleted: true }),
                { undo: { userMessage: 'Series deleted', disableUndoAction: true } },
              ),
            );
          },
          denyLabel: 'Cancel',
          denyButtonVariant: QButtonVariantEnum.DEFAULT,
        }),
      ),
    });
    closeMenu();
    tracker.track(tracker.events.recurringMenu, { action: 'deleteSeries', variant, context: id });
  };

  const handleViewSeries = () => {
    dispatch(
      showViewSeriesDialog({
        ...(Record.isRecord(model.transaction) ? model.transaction.toJS() : model.transaction),
        stModelId: model.id,
      } as TransactionProps),
    );
    closeMenu();
    tracker.track(tracker.events.recurringMenu, { action: 'viewSeries', variant, context: id });
  };

  const handleEditSeries = () => {
    setEditing(true);
    tracker.track(tracker.events.recurringMenu, { action: 'editSeries', variant, context: id });
  };

  const handleConnectSeries = () => {
    // TODO - @LUC - if BVA, open edit series with bill-connect tab open
    dispatch(doBillPresentment(model, location));
    tracker.track(reconnect ? tracker.events.fixBillerStart : tracker.events.addBillerStart, { initiator: location });
    closeMenu();
    tracker.track(tracker.events.recurringMenu, { action: 'connectSeries', variant, context: id });
  };

  const handleMenuSelect = (callback: () => void) => {
    callback();
    closeMenu();
  };

  const closeEditCheck = (isDirty: boolean) => {
    if (!model?.id) {
      // scroll into view?
      // if (selectedRowRef) {
      //   setTimeout(() => {
      //     // we wait for the slide transition to finish before we scroll to the element
      //     if (selectedRowRef.scrollIntoView) {
      //       selectedRowRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      //     } else if (selectedRowRef.scrollIntoViewIfNeeded) {
      //       selectedRowRef.scrollIntoViewIfNeeded(true);
      //     }
      //   }, 500)
      // }
    }

    if (isDirty) {
      dialogAlert(
        'Just checking...',
        'Are you sure you want to discard all changes?',
        (btnObj: any) => {
          if (btnObj.btnPressed === 'Discard') {
            setEditing(false);
          }
        },
        ['Go Back', 'Discard'],
      );
    } else {
      setEditing(false);
    }
  };

  const unlink = () => {
    makeDialog({
      id: 'unlink-series-confirmation-settings',
      type: DIALOG_CONFIRMATION,
      props: ImmutableMap(
        mkConfirmationDialogProps({
          title: 'Disconnect Biller',
          content: `Your ${model?.transaction?.payee} 
            bill reminders will no longer be updated with the latest bill information. `,
          confirmLabel: 'Disconnect',
          confirmAction: (_reason, _closeEvent) => {
            HandleDisconnectOption(dispatch, model, location, true);
          },
          denyLabel: 'Cancel',
        }),
      ),
    });
    tracker.track(tracker.events.recurringMenu, { action: 'disconnectSeries', variant, context: id });
  };

  const menuOptions = isBillerConnecting
    ? []
    : model?.isCompleted
      ? [
          { label: 'Delete series', value: confirmDeleteSeries },
          { label: 'View series', value: handleViewSeries },
        ]
      : [
          { label: 'Edit series', value: handleEditSeries },
          { label: 'View series', value: handleViewSeries },
          variant !== 'tax' && billText ? { label: billText, value: handleConnectSeries } : null,
          hasBPS ? { label: 'Disconnect biller', value: unlink } : null,
          { label: 'Delete series', value: confirmDeleteSeries },
          variant !== 'tax' ? { label: 'End series', value: confirmEndSeries } : null,
        ];

  return (
    <div role='presentation' onClick={(e) => e.stopPropagation()}>
      <IconButton
        disabled={!exist || isBillerConnecting}
        id={id ?? `scheduled-txn-${index}-menu`}
        onClick={openMenu}
        ref={menuRef}
        size='large'
        style={{ color: theme.palette.icon.main }}
        {...slotProps?.iconButton}
      >
        <ThreeDotIcon />
      </IconButton>

      <NestedQMenu
        open={menuOpen}
        options={menuOptions}
        anchorEl={menuRef.current}
        name='settings-series-options-menu'
        onClose={closeMenu}
        onChange={handleMenuSelect}
      />

      {editing && (
        <ScheduledTransactionEditDialog
          open={editing}
          key={model ? model.clientId || model.id : 'noKey'}
          scheduledTransaction={model}
          onClose={closeEditCheck}
        />
      )}
    </div>
  );
}

RecurringMenu.displayName = 'RecurringMenu';

export default RecurringMenu;
